import { Fragment, Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';

import { useGetRepaymentsPanelData } from './useGetRepaymentsPanelData';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import MainContentPending from '../../../pageStructure/mainContentPending';
import RepayEarlyForFree from '../../OutstandingView/dashboardActions/RepayEarlyForFree/RepayEarlyForFree';
import FutureRepayments from '../futureRepayment/FutureRepayments';
import { OverduePayments } from '../overduePayments/OverduePayments';
import PastRepayments from '../pastRepayments/PastRepayments';
import { RCFNextRepayment } from '../RCFNextRepayment/RCFNextRepayment';
import { RCFRenewal } from '../RCFRenewal/RCFRenewal';
import { TopUpTile } from '../topUpTile/TopUpTile';

type Country = ReturnType<typeof getCountry>;
type WidgetKey = 'overdue' | 'topup' | 'future' | 'past' | 'repayEarly';

const WIDGET_ORDER: Record<Country, WidgetKey[]> = {
    UK: ['overdue', 'topup', 'future', 'past', 'repayEarly'],
    DE: ['topup', 'future', 'past', 'repayEarly', 'overdue']
};

export const RepaymentsPanel = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    return (
        <Suspense fallback={<MainContentPending />}>
            <Repaymentswidgets accountId={accountId} />
        </Suspense>
    );
};

const Repaymentswidgets = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const {
        isOverdue,
        isRBL,
        isRCF,
        topUpStatus,
        inFirstRepaymentTest,
        requestedCashAmount,
        requestedAmount,
        canTopUpAfterRepayments,
        isMicroloan,
        amountToRepayBeforeTopUp
    } = useGetRepaymentsPanelData(accountId);

    if (isRCF)
        return (
            <div data-testid="rcf-repayments-panel" className="space-y-l">
                {isOverdue && <OverduePayments accountId={accountId} />}
                <RCFNextRepayment accountId={accountId} />
                {!isOverdue && <RepayEarlyForFree accountId={accountId} />}
                <RCFRenewal />
            </div>
        );

    const widgetConfig: Record<
        WidgetKey,
        { isVisible: boolean; element: React.ReactNode }
    > = {
        overdue: {
            isVisible: isOverdue,
            element: <OverduePayments accountId={accountId} />
        },
        topup: {
            isVisible: true,
            element: (
                <TopUpTile
                    accountId={accountId}
                    topUpStatus={topUpStatus}
                    inFirstRepaymentTest={inFirstRepaymentTest}
                    isOverdue={isOverdue}
                    requestedCashAmount={requestedCashAmount}
                    requestedAmount={requestedAmount}
                    canTopUpAfterRepayments={canTopUpAfterRepayments}
                    isMicroloan={isMicroloan}
                    amountToRepayBeforeTopUp={amountToRepayBeforeTopUp}
                />
            )
        },
        future: {
            isVisible: !isRBL,
            element: <FutureRepayments accountId={accountId} />
        },
        past: {
            isVisible: true,
            element: <PastRepayments accountId={accountId} />
        },
        repayEarly: {
            isVisible: !isOverdue && !isRBL,
            element: <RepayEarlyForFree accountId={accountId} />
        }
    };

    const orderedWidgets = WIDGET_ORDER[getCountry()];

    return (
        <div
            data-testid="repayments-panel"
            className="col-span-5 flex flex-col gap-l max-sm:col-span-full"
        >
            {orderedWidgets.map((key) => {
                const { isVisible, element } = widgetConfig[key];
                return isVisible ? (
                    <Fragment key={key}>{element}</Fragment>
                ) : null;
            })}
        </div>
    );
};
